"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.activeThemeFromSnapshot = activeThemeFromSnapshot;
exports.motionSpecsFromSnapshot = motionSpecsFromSnapshot;
exports.stylePresetsFromSnapshot = stylePresetsFromSnapshot;
exports.styleVariantsFromSnapshot = styleVariantsFromSnapshot;
exports.customSheetsFromSnapshot = customSheetsFromSnapshot;
exports.customHtmlBlocksFromSnapshot = customHtmlBlocksFromSnapshot;
exports.buildPresetRowIdToPresetIdMap = buildPresetRowIdToPresetIdMap;
exports.createMotionSpecCells = createMotionSpecCells;
exports.createStylePresetCells = createStylePresetCells;
exports.createStyleVariantCells = createStyleVariantCells;
exports.createThemeCells = createThemeCells;
exports.createCustomSheetCells = createCustomSheetCells;
exports.createCustomHtmlBlockCells = createCustomHtmlBlockCells;
const custom_app_config_1 = require("./custom-app-config");
// =============================================================================
// Cell readers
// =============================================================================
function readCellByName(row, columns, name) {
    const column = columns.find(item => item.name === name);
    return column ? row.cells[column.id] : undefined;
}
function readCellAsString(row, columns, name) {
    const value = readCellByName(row, columns, name);
    if (value == null)
        return null;
    const text = String(value).trim();
    return text || null;
}
function readCellAsBoolean(row, columns, name) {
    const value = readCellByName(row, columns, name);
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'string') {
        const normalised = value.trim().toLowerCase();
        return normalised === 'true' || normalised === '1';
    }
    return false;
}
function readCellAsInt(row, columns, name, fallback = 0) {
    const raw = readCellAsString(row, columns, name);
    if (!raw)
        return fallback;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
}
function parseJsonRecord(value) {
    if (!value)
        return {};
    try {
        const parsed = JSON.parse(value);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const result = {};
        for (const [key, entry] of Object.entries(parsed)) {
            if (typeof entry === 'string')
                result[key] = entry;
        }
        return result;
    }
    catch {
        return {};
    }
}
function parseStringArray(value) {
    if (!value)
        return [];
    try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed))
            return [];
        return parsed.filter((entry) => typeof entry === 'string');
    }
    catch {
        return [];
    }
}
function parseTokenRecord(value) {
    const raw = parseJsonRecord(value);
    const result = {};
    for (const [key, entry] of Object.entries(raw)) {
        const tokenName = custom_app_config_1.StyleTokenNameSchema.safeParse(key);
        const tokenValue = custom_app_config_1.StyleTokenValueSchema.safeParse(entry);
        if (tokenName.success && tokenValue.success) {
            result[tokenName.data] = tokenValue.data;
        }
    }
    return result;
}
// =============================================================================
// Snapshot → domain mappers
// =============================================================================
/**
 * Pick the single active theme row, or `null` when no row is flagged
 * `active=true`. When multiple rows claim `active` (CRDT race) the most
 * recently updated row wins so the editor converges deterministically.
 */
function activeThemeFromSnapshot(snapshot) {
    const candidates = snapshot.rows
        .filter(row => readCellAsBoolean(row, snapshot.columns, 'active'))
        .map(row => ({
        row,
        updatedAt: readCellAsString(row, snapshot.columns, 'updatedAt') ?? '',
    }))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    const winner = candidates[0]?.row;
    if (!winner)
        return null;
    return {
        id: winner.id,
        name: readCellAsString(winner, snapshot.columns, 'name') ?? 'Theme',
        tokens: parseJsonRecord(readCellAsString(winner, snapshot.columns, 'tokens')),
        customCss: readCellAsString(winner, snapshot.columns, 'customCss') ?? '',
        aiThreadId: readCellAsString(winner, snapshot.columns, 'aiThreadId') ?? null,
        active: true,
    };
}
function motionSpecsFromSnapshot(snapshot) {
    const specs = [];
    for (const row of snapshot.rows) {
        const candidate = {
            id: readCellAsString(row, snapshot.columns, 'specId') ?? row.id,
            preset: readCellAsString(row, snapshot.columns, 'preset'),
            trigger: readCellAsString(row, snapshot.columns, 'trigger'),
            easing: readCellAsString(row, snapshot.columns, 'easing'),
            durationMs: readCellAsInt(row, snapshot.columns, 'durationMs'),
            delayMs: readCellAsInt(row, snapshot.columns, 'delayMs'),
            staggerMs: readCellAsInt(row, snapshot.columns, 'staggerMs', 0),
            reducedMotionPolicy: readCellAsString(row, snapshot.columns, 'reducedMotionPolicy'),
        };
        const parsed = custom_app_config_1.MotionSpecSchema.safeParse(candidate);
        if (parsed.success)
            specs.push(parsed.data);
    }
    return specs;
}
function stylePresetsFromSnapshot(snapshot) {
    const presets = [];
    for (const row of snapshot.rows) {
        const tokens = parseTokenRecord(readCellAsString(row, snapshot.columns, 'tokens'));
        const candidate = {
            id: readCellAsString(row, snapshot.columns, 'presetId') ?? row.id,
            target: readCellAsString(row, snapshot.columns, 'target'),
            surface: readCellAsString(row, snapshot.columns, 'surface') ?? 'quiet',
            density: tokens.padding ?? 'standard',
            elevation: tokens.shadow ?? 'none',
            radius: tokens.radius ?? 'none',
            mediaTreatment: tokens.mediaRatio ?? 'none',
            typographyScale: tokens.fontScale ?? 'body',
            ctaTreatment: tokens.ctaRadius ?? 'link',
            contentRhythm: readCellAsString(row, snapshot.columns, 'contentRhythm') ?? 'normal',
        };
        const parsed = custom_app_config_1.StylePresetSchema.safeParse(candidate);
        if (parsed.success)
            presets.push(parsed.data);
    }
    return presets;
}
function styleVariantsFromSnapshot(snapshot, presetRowIdToPresetId) {
    const variants = [];
    for (const row of snapshot.rows) {
        const presetRowId = readCellAsString(row, snapshot.columns, 'preset');
        const presetId = presetRowId
            ? presetRowIdToPresetId.get(presetRowId)
            : undefined;
        if (!presetId)
            continue;
        const stateRaw = readCellAsString(row, snapshot.columns, 'state');
        const candidate = {
            id: readCellAsString(row, snapshot.columns, 'variantId') ?? row.id,
            presetId,
            state: stateRaw && stateRaw !== 'none' ? stateRaw : null,
            media: readCellAsString(row, snapshot.columns, 'media') ?? 'base',
            tokens: parseTokenRecord(readCellAsString(row, snapshot.columns, 'tokens')),
        };
        const parsed = custom_app_config_1.StyleVariantSchema.safeParse(candidate);
        if (parsed.success)
            variants.push(parsed.data);
    }
    return variants;
}
function customSheetsFromSnapshot(snapshot) {
    return snapshot.rows
        .map(row => ({
        id: row.id,
        name: readCellAsString(row, snapshot.columns, 'name') ?? 'Sheet',
        content: readCellAsString(row, snapshot.columns, 'content') ?? '',
        enabled: readCellAsBoolean(row, snapshot.columns, 'enabled'),
        order: readCellAsInt(row, snapshot.columns, 'order'),
        media: readCellAsString(row, snapshot.columns, 'media') ?? 'screen',
        externalUrl: readCellAsString(row, snapshot.columns, 'externalUrl'),
    }))
        .sort((a, b) => a.order - b.order);
}
function customHtmlBlocksFromSnapshot(snapshot) {
    return snapshot.rows.map(row => ({
        id: row.id,
        name: readCellAsString(row, snapshot.columns, 'name') ?? 'Block',
        template: readCellAsString(row, snapshot.columns, 'template') ?? '',
        dataSourceRef: readCellAsString(row, snapshot.columns, 'dataSourceRef'),
        usedIn: parseStringArray(readCellAsString(row, snapshot.columns, 'usedIn')),
    }));
}
/**
 * Build a `presetRowId → presetId` map so `styleVariantsFromSnapshot` can
 * resolve the relation cell back to the business-id of the parent preset.
 * The relation column on the variant row holds the preset's Y-Doc row id
 * (BlockSuite relations are id-based) — the business id is stored in the
 * preset's `presetId` cell.
 */
function buildPresetRowIdToPresetIdMap(presetsSnapshot) {
    const presetIdColumn = presetsSnapshot.columns.find(column => column.name === 'presetId');
    if (!presetIdColumn)
        return new Map();
    const map = new Map();
    for (const row of presetsSnapshot.rows) {
        const value = row.cells[presetIdColumn.id];
        if (typeof value === 'string' && value) {
            map.set(row.id, value);
        }
    }
    return map;
}
// =============================================================================
// Domain → cell encoders (write side)
// =============================================================================
function createMotionSpecCells(spec) {
    return {
        specId: spec.id,
        preset: spec.preset,
        trigger: spec.trigger,
        easing: spec.easing,
        durationMs: String(spec.durationMs),
        delayMs: String(spec.delayMs),
        staggerMs: String(spec.staggerMs),
        reducedMotionPolicy: spec.reducedMotionPolicy,
        componentScope: '',
    };
}
function createStylePresetCells(preset) {
    return {
        presetId: preset.id,
        target: preset.target,
        surface: preset.surface,
        contentRhythm: preset.contentRhythm,
        tokens: JSON.stringify({
            padding: preset.density,
            shadow: preset.elevation,
            radius: preset.radius,
            mediaRatio: preset.mediaTreatment,
            fontScale: preset.typographyScale,
            ctaRadius: preset.ctaTreatment,
        }),
    };
}
function createStyleVariantCells(variant) {
    return {
        variantId: variant.id,
        state: variant.state ?? 'none',
        media: variant.media,
        tokens: JSON.stringify(variant.tokens),
    };
}
function createThemeCells(theme) {
    const nowIso = new Date().toISOString();
    return {
        name: theme.name,
        tokens: JSON.stringify(theme.tokens),
        customCss: theme.customCss,
        aiThreadId: theme.aiThreadId ?? '',
        active: theme.active,
        createdAt: nowIso,
        updatedAt: nowIso,
    };
}
function createCustomSheetCells(sheet) {
    return {
        name: sheet.name,
        content: sheet.content,
        enabled: sheet.enabled,
        order: String(sheet.order),
        media: sheet.media,
        externalUrl: sheet.externalUrl ?? '',
    };
}
function createCustomHtmlBlockCells(block) {
    return {
        name: block.name,
        template: block.template,
        dataSourceRef: block.dataSourceRef ?? '',
        usedIn: JSON.stringify(Array.from(new Set(block.usedIn))),
    };
}
